/**
 * FamilyHistoryEditor — Phase 6
 *
 * CRUD for family history entries.
 * ICD codes optional but validated when provided (shows ✓ or ⚠).
 * Follows exact same patterns as SmartProblemListEditor (Phase 4).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Plus, X, RefreshCw, Users, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const RELATIONSHIPS = [
  "mother","father","maternal_grandmother","maternal_grandfather",
  "paternal_grandmother","paternal_grandfather","sibling","brother","sister",
  "son","daughter","maternal_aunt","maternal_uncle","paternal_aunt","paternal_uncle",
  "half_sibling","other",
];

function RelationshipLabel({ rel }) {
  return <span className="capitalize text-xs font-semibold">{(rel || '').replace(/_/g, ' ')}</span>;
}

function ConditionChip({ cond }) {
  const isValid = cond.icd_validated;
  const hasCode = !!cond.icd_code;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-card">
      {cond.condition_name || cond.icd_code}
      {hasCode && (
        <span className={`font-mono ${isValid ? 'text-emerald-600' : 'text-amber-600'}`}>
          {cond.icd_code}
          {isValid ? <CheckCircle2 className="w-2.5 h-2.5 inline ml-0.5" /> : <AlertTriangle className="w-2.5 h-2.5 inline ml-0.5" />}
        </span>
      )}
    </span>
  );
}

function ConditionInput({ condition, onChange, onRemove, repo }) {
  const [icqQuery, setIcqQuery] = useState(condition.icd_code || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get('/terminology/icd10/search', { params: { q, limit: 5 } });
      setResults(res.data.results || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => search(icqQuery), 300);
    return () => clearTimeout(debRef.current);
  }, [icqQuery, search]);

  return (
    <div className="flex items-center gap-1.5 mb-1.5">
      <input type="text" placeholder="Condition name" value={condition.condition_name || ''}
        onChange={e => onChange({ ...condition, condition_name: e.target.value })}
        className="text-xs border rounded px-2 py-1 bg-background flex-1" />
      <div className="relative w-28">
        <input type="text" placeholder="ICD code" value={icqQuery}
          onChange={e => { setIcqQuery(e.target.value); onChange({ ...condition, icd_code: e.target.value }); }}
          className="w-full text-[10px] border rounded px-1.5 py-1 bg-background font-mono" />
        {searching && <RefreshCw className="w-2.5 h-2.5 animate-spin absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground" />}
        {results.length > 0 && (
          <div className="absolute left-0 top-full mt-0.5 w-56 bg-card border rounded shadow-lg z-20 max-h-32 overflow-y-auto">
            {results.map(r => (
              <button key={r.code_normalized}
                onClick={() => {
                  const code = r.code || r.code_normalized;
                  setIcqQuery(code);
                  onChange({ ...condition, icd_code: code, condition_name: condition.condition_name || r.description });
                  setResults([]);
                }}
                className="w-full flex items-center gap-1.5 px-2 py-1 text-left text-[10px] hover:bg-accent">
                <span className="font-mono">{r.code || r.code_normalized}</span>
                <span className="truncate flex-1 text-muted-foreground">{r.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <input type="number" placeholder="Age" value={condition.age_at_onset || ''}
        onChange={e => onChange({ ...condition, age_at_onset: e.target.value ? parseInt(e.target.value) : null })}
        className="text-[10px] border rounded px-1.5 py-1 bg-background w-14" min="0" max="120" />
      <button onClick={onRemove} className="text-muted-foreground hover:text-red-500 p-0.5">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function FamilyMemberCard({ entry, patientId, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Remove this family member?')) return;
    setDeleting(true);
    try {
      await api.delete(`/patients/${patientId}/family-history/${entry.id}`);
      toast.success('Removed');
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally { setDeleting(false); }
  };

  return (
    <div className="border-b last:border-0">
      <div className="flex items-start justify-between px-4 py-3 hover:bg-accent/20 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <RelationshipLabel rel={entry.relationship} />
            {entry.is_deceased && (
              <Badge variant="outline" className="text-[8px] bg-slate-50 text-slate-500 border-slate-200">Deceased</Badge>
            )}
            {entry.age_at_death && (
              <span className="text-[9px] text-muted-foreground">age {entry.age_at_death}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {(entry.conditions || []).length === 0 ? (
              <span className="text-[10px] text-muted-foreground italic">No conditions recorded</span>
            ) : (
              (entry.conditions || []).map(c => <ConditionChip key={c.id} cond={c} />)
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          <button onClick={() => setExpanded(e => !e)}
            className="text-muted-foreground hover:text-foreground p-0.5">
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          <button onClick={handleDelete} disabled={deleting}
            className="text-muted-foreground hover:text-red-500 p-0.5" data-testid={`fh-delete-${entry.id}`}>
            {deleting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {expanded && entry.notes && (
        <p className="px-4 pb-2 text-[10px] text-muted-foreground italic">{entry.notes}</p>
      )}
    </div>
  );
}

function AddFamilyMemberPanel({ patientId, onAdded, onCancel }) {
  const [relationship, setRelationship] = useState('');
  const [conditions, setConditions] = useState([]);
  const [isDeceased, setIsDeceased] = useState(false);
  const [ageAtDeath, setAgeAtDeath] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!relationship) { toast.error('Select a relationship'); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/family-history`, {
        relationship, conditions, is_deceased: isDeceased,
        age_at_death: ageAtDeath ? parseInt(ageAtDeath) : null, notes,
      });
      toast.success('Family member added');
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t bg-accent/5 p-3 space-y-2" data-testid="add-family-member-panel">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Add Family Member</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Relationship</label>
          <select value={relationship} onChange={e => setRelationship(e.target.value)}
            className="w-full mt-0.5 text-xs border rounded px-2 py-1.5 bg-background capitalize"
            data-testid="fh-relationship-select">
            <option value="">Select...</option>
            {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={isDeceased} onChange={e => setIsDeceased(e.target.checked)} />
            Deceased
          </label>
          {isDeceased && (
            <input type="number" placeholder="Age at death" value={ageAtDeath}
              onChange={e => setAgeAtDeath(e.target.value)}
              className="text-[10px] border rounded px-1.5 py-1 bg-background w-24" min="0" max="130" />
          )}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Conditions</label>
          <button onClick={() => setConditions(prev => [...prev, {id: Date.now().toString(), condition_name:'', icd_code:''}])}
            className="text-[9px] text-primary hover:text-primary/80 flex items-center gap-0.5">
            <Plus className="w-3 h-3" /> Add condition
          </button>
        </div>
        {conditions.map((c, i) => (
          <ConditionInput key={c.id} condition={c}
            onChange={updated => setConditions(prev => prev.map((x,j) => j===i ? updated : x))}
            onRemove={() => setConditions(prev => prev.filter((_,j) => j !== i))} />
        ))}
      </div>
      <input type="text" placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)}
        className="w-full text-xs border rounded px-2 py-1.5 bg-background" />
      <div className="flex gap-1.5">
        <Button size="sm" className="text-xs gap-1 h-7" onClick={handleSave} disabled={saving || !relationship}
          data-testid="fh-save-btn">
          {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          Save Family Member
        </Button>
        <Button size="sm" variant="ghost" className="text-xs h-7" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export function FamilyHistoryEditor({ patientId, initialHistory }) {
  const [history, setHistory] = useState(initialHistory || []);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await api.get(`/patients/${patientId}/family-history`);
      setHistory(res.data.family_history || []);
    } catch {}
    finally { setLoading(false); }
  }, [patientId]);

  return (
    <div data-testid="family-history-editor">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Family History
            <Badge variant="outline" className="text-[10px] ml-auto">{history.length}</Badge>
            {!showAdd && (
              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 ml-1"
                onClick={() => setShowAdd(true)} data-testid="show-add-fh-btn">
                <Plus className="w-2.5 h-2.5" /> Add
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-xs text-muted-foreground px-4 py-4">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-muted-foreground px-4 py-4">No family history recorded</p>
          ) : (
            history.map(entry => (
              <FamilyMemberCard key={entry.id} entry={entry} patientId={patientId} onUpdated={refresh} />
            ))
          )}
          {showAdd && (
            <AddFamilyMemberPanel patientId={patientId}
              onAdded={() => { setShowAdd(false); refresh(); }}
              onCancel={() => setShowAdd(false)} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
